import { Box } from '@material-ui/core'
import { unwrapResult } from '@reduxjs/toolkit'
import GoBack from 'components/GoBack'
import FitContentPaper from 'components/Paper/FitContentPaper'
import { FormikHelpers } from 'formik'
import useRestricter from 'hooks/useRestricter'
import { useSnackbar } from 'notistack'
import React, { useEffect } from 'react'
import { RouteConfigComponentProps } from 'react-router-config'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { offeringListThunk, selectCurrentTempleOfferings } from 'store/reducers/temple/offeringSlice'
import {
    selectCurrentTempleStaffProfiles,
    selectStaffProfileById,
    selectStaffProfileStatus,
    staffProfileListThunk,
    staffProfileRetrieveThunk,
    staffProfileUpdateThunk,
} from 'store/reducers/temple/staffProfileSlice'
import { selectAllUsers, usersListThunk } from 'store/reducers/temple/usersSlice'
import { StaffProfilePayloadType } from 'types/api/temple'
import { getFieldErrors } from 'utils/api'
import { PERMISSIONS, RESTRICTED_PATHS } from 'utils/permissions'
import FormStaffProfile from './FormStaffProfile'

interface RouteParams {
    id: string
}

type StaffProfileFormType = StaffProfilePayloadType & {
    languageText: string
}

const EditStaffProfile: React.FC<RouteConfigComponentProps<RouteParams>> = props => {
    const { id } = props.match.params
    const dispatch = useAppDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const api = useAppSelector(selectStaffProfileStatus)
    const users = useAppSelector(selectAllUsers)
    const profiles = useAppSelector(selectCurrentTempleStaffProfiles)
    const profile = useAppSelector(state => selectStaffProfileById(state, id))
    const offerings = useAppSelector(selectCurrentTempleOfferings)
    const hasEditPermission = useRestricter(PERMISSIONS.CHANGE, RESTRICTED_PATHS.STAFF_PROFILES)

    useEffect(() => {
        dispatch(usersListThunk())
        dispatch(staffProfileListThunk({}))
        dispatch(staffProfileRetrieveThunk({ id }))
        dispatch(offeringListThunk({}))
    }, [dispatch, id])

    const handleSubmit = (data: StaffProfilePayloadType, actions: FormikHelpers<StaffProfileFormType>) => {
        dispatch(staffProfileUpdateThunk({ id, data }))
            .then(unwrapResult)
            .then(response => enqueueSnackbar(`${response.fullName} profile updated`, { variant: 'success' }))
            .catch(err => {
                Object.entries(getFieldErrors(data, err)).forEach(([key, value]) => actions.setFieldError(key, value))
            })
            .finally(() => actions.setSubmitting(false))
    }

    if (!hasEditPermission) return null

    return (
        <GoBack {...props}>
            <FitContentPaper>
                <Box component='h4' mb={4}>Edit Staff/Priest Profile</Box>
                <FormStaffProfile
                    initialValues={profile}
                    users={users}
                    profiles={profiles}
                    offerings={offerings}
                    buttonLabel='Update'
                    api={api}
                    onSubmit={handleSubmit}
                />
            </FitContentPaper>
        </GoBack>
    )
}

export default EditStaffProfile
